import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useAuthStore } from './stores/authStore';
import DashboardLayout from './components/Layout/DashboardLayout';
import LoadingSpinner from './components/Common/LoadingSpinner';
import ErrorBoundary from './components/Common/ErrorBoundary';
import { canvasRoutes, canvasFullscreenRoutes } from './canvas/routes';

const Login = lazy(() => import('./pages/Login'));
const OperationsCenter = lazy(() => import('./pages/OperationsCenter'));
const ScreenDirectory = lazy(() => import('./pages/ScreenDirectory'));
const ScreenMissing = lazy(() => import('./pages/ScreenMissing'));
const Dashboard = lazy(() => import('./pages/Dashboard'));

const OrderList = lazy(() => import('./pages/Orders/OrderList'));
const OrderCreate = lazy(() => import('./pages/Orders/OrderCreate'));
const OrderDetail = lazy(() => import('./pages/Orders/OrderDetail'));
const OrderVerification = lazy(() => import('./pages/Orders/OrderVerification'));

const InventoryList = lazy(() => import('./pages/Inventory/InventoryList'));
const ProductFormPage = lazy(() => import('./pages/Inventory/ProductFormPage'));
const ProductDetail = lazy(() => import('./pages/Inventory/ProductDetail'));
const LotTracking = lazy(() => import('./pages/Inventory/LotTracking'));
const InventoryOperationsPage = lazy(() => import('./pages/Inventory/InventoryOperationsPage'));
const PurchasingDashboard = lazy(() => import('./pages/Purchasing/PurchasingDashboard'));
const PurchaseOrderDetail = lazy(() => import('./pages/Purchasing/PurchaseOrderDetail'));
const ReceivingReadinessPage = lazy(() => import('./pages/Purchasing/ReceivingReadinessPage'));
const SupplierReceivingPage = lazy(() => import('./pages/Purchasing/SupplierReceivingPage'));
const PackingQueuePage = lazy(() => import('./pages/Packing/PackingQueuePage'));
const PackingOrderPage = lazy(() => import('./pages/Packing/PackingOrderPage'));
const PackingCartonOpenPage = lazy(() => import('./pages/Packing/PackingCartonOpenPage'));
const PackingCartonPage = lazy(() => import('./pages/Packing/PackingCartonPage'));

const Locations = lazy(() => import('./pages/Warehouse/Locations'));
const DeliveryList = lazy(() => import('./pages/Delivery/DeliveryList'));
const ReportsDashboard = lazy(() => import('./pages/Reports/ReportsDashboard'));
const WaveList = lazy(() => import('./pages/Waves/WaveList'));
const WaveDetail = lazy(() => import('./pages/Waves/WaveDetail'));
const WavePlanningPage = lazy(() => import('./canvas/pages/picking/WavePlanningPage'));
const QCDashboard = lazy(() => import('./pages/QC/QCDashboard'));
const QCInspection = lazy(() => import('./pages/QC/QCInspection'));
const DockSchedule = lazy(() => import('./pages/Dock/DockSchedule'));
const FleetDashboard = lazy(() => import('./pages/Fleet/FleetDashboard'));
const PreTripInspection = lazy(() => import('./pages/Fleet/PreTripInspection'));
const MarineX = lazy(() => import('./pages/MarineX/MarineX'));
const UserManagement = lazy(() => import('./pages/Settings/UserManagement'));
const WarehouseSettings = lazy(() => import('./pages/Settings/WarehouseSettings'));
const IntegrationSettings = lazy(() => import('./pages/Settings/IntegrationSettings'));
const StationApp = lazy(() => import('./station/StationApp'));
// --- slice: prospecting (unified Florida B2B Prospecting Workspace; managers and admins) ---
const ProspectingPage = lazy(() => import('./pages/Prospecting/ProspectingPage'));

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const checkAuth = useAuthStore((state) => state.checkAuth);

  // Resolve the session on entry; without this, a logged-out visit never leaves the spinner.
  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  // Only block on the first resolution: a signed-in layout must not unmount while useAuth()
  // re-checks the session, or Header's check would remount it in a loop.
  if (isLoading && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-50" aria-live="polite">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PageLoader() {
  return (
    <div className="flex min-h-[400px] items-center justify-center" aria-live="polite">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Canvas SCR-000: Home is what opens first. */}
            <Route index element={<Navigate to="/home" replace />} />
            <Route path="work" element={<OperationsCenter />} />
            <Route path="screens" element={<ScreenDirectory />} />
            <Route path="dashboard" element={<Dashboard />} />

            <Route path="orders" element={<OrderList />} />
            <Route path="orders/new" element={<OrderCreate />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="orders/:id/verify" element={<OrderVerification />} />

            <Route path="inventory" element={<InventoryList />} />
            <Route path="inventory/new" element={<ProductFormPage />} />
            <Route path="inventory/lots" element={<LotTracking />} />
            <Route path="inventory/control" element={<InventoryOperationsPage view="overview" />} />
            <Route path="inventory/expiry-risk" element={<InventoryOperationsPage view="expiry" />} />
            <Route path="inventory/movements" element={<InventoryOperationsPage view="movements" />} />
            <Route path="inventory/:id/edit" element={<ProductFormPage />} />
            <Route path="inventory/:id" element={<ProductDetail />} />

            <Route path="packing" element={<PackingQueuePage />} />
            <Route path="packing/orders/:orderId" element={<PackingOrderPage />} />
            <Route path="packing/cartons" element={<PackingCartonOpenPage />} />
            <Route path="packing/cartons/:cartonId" element={<PackingCartonPage />} />
            <Route path="purchasing" element={<PurchasingDashboard />} />
            <Route path="purchasing/receiving" element={<ReceivingReadinessPage />} />
            <Route path="purchasing/supplier-inbound" element={<SupplierReceivingPage />} />
            <Route path="purchasing/orders/:id" element={<PurchaseOrderDetail />} />

            <Route path="warehouse/locations" element={<Locations />} />
            <Route path="delivery" element={<DeliveryList />} />
            {/* slice: picking: SCR-090 wave planning is the working page at /waves; legacy list kept at /waves/legacy */}
            <Route path="waves" element={<WavePlanningPage />} />
            <Route path="waves/legacy" element={<WaveList />} />
            <Route path="waves/:id" element={<WaveDetail />} />
            <Route path="qc" element={<QCDashboard />} />
            <Route path="qc/inspection/:id" element={<QCInspection />} />
            <Route path="dock" element={<DockSchedule />} />
            <Route path="fleet" element={<FleetDashboard />} />
            <Route path="fleet/inspection/:vehicleId" element={<PreTripInspection />} />
            <Route path="reports" element={<ReportsDashboard />} />
            <Route path="marinex" element={<MarineX />} />
            <Route path="settings/users" element={<UserManagement />} />
            <Route path="settings/warehouse" element={<WarehouseSettings />} />
            <Route path="settings/integrations" element={<IntegrationSettings />} />

            {/* slice: prospecting: accounts, contacts, pipeline and field routes (docs/slices/prospecting.md) */}
            <Route path="customers/prospecting" element={<ProspectingPage tab="map" />} />
            <Route path="customers/prospecting/pipeline" element={<ProspectingPage tab="pipeline" />} />
            <Route path="customers/prospecting/directory" element={<ProspectingPage tab="directory" />} />
            <Route path="customers/prospecting/routes" element={<ProspectingPage tab="routes" />} />
            <Route path="customers/prospecting/search" element={<ProspectingPage tab="search" />} />
            <Route path="customers/prospecting/analytics" element={<ProspectingPage tab="analytics" />} />
            <Route path="customers/prospecting/data" element={<ProspectingPage tab="data" />} />

            {/* Canvas UI shells: one route per web screen on the Core Screens canvas. */}
            {canvasRoutes()}
            <Route path="*" element={<ScreenMissing />} />
          </Route>

          {/* TV wallboards: full screen, no sidebar. */}
          {canvasFullscreenRoutes().map((r) => (
            <Route
              key={r.key}
              path={r.props.path}
              element={<ProtectedRoute>{r.props.element}</ProtectedRoute>}
            />
          ))}

          {/* Tablet station mode (installable PWA), bound to one work point. */}
          <Route
            path="/station/*"
            element={
              <ProtectedRoute>
                <StationApp />
              </ProtectedRoute>
            }
          />

          {/* Unknown links fail closed instead of redirecting to an unrelated workflow. */}
          <Route path="*" element={<ScreenMissing />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
